/* eslint max-lines: 0 */
import { useAppDispatch, useAppSelector } from 'app/store';
import { useIsMounted, useResource } from 'app/utils/hooks';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import {
  fetchSongAudioTypesEffect,
  fetchSongsWithAudioListSearchValidationSchema,
} from 'app/store/SongList/effects';
import {
  getSongAudioListValidation,
  getSongAudioTypes,
} from 'app/store/SongList/selectors';
import { addAlertAction } from 'app/store/Alerts/slice';
import { setSelectedAudioCountAction } from 'app/store/Mixpanel/slice';
import {
  AudioListFilters,
  AudioSelectionItem,
  SelectedAudioObject,
  ValidationContext,
  getInvalidAudioErrorMessage,
  isAudioListScrollbarVisibile,
  AudioInvalidSelectionDialog,
} from 'app/components/Audio';
import { audioToSelectedAudioObject } from 'app/components/Song/Audio/CreatePlaylistDialog/utils';
import { DisabledAudios } from 'types/general.types';
import { useFetchAudio } from './hooks';
import { AudioSelectListComponent } from './AudioSelectListComponent';

interface AudioSelectListProps {
  onCancel: () => void;
  onAdd: (audios: SelectedAudioObject) => Promise<void>;
  emptyStateMessage: string;
  audioTypesFilterKeys?: string[];
  maxAudioCount?: number;
  loading?: boolean;
  preselectedAudio?: AudioSelectionItem;
  disabledAudios?: DisabledAudios;
  validationContext?: ValidationContext;
  checkAllOptions?: string[];
}

export const AudioSelectList = ({
  onCancel,
  onAdd,
  emptyStateMessage,
  audioTypesFilterKeys,
  maxAudioCount,
  loading: loadingProp,
  preselectedAudio,
  disabledAudios,
  validationContext,
  checkAllOptions,
}: AudioSelectListProps) => {
  const dispatch = useAppDispatch();
  const [checked, setChecked] = useState<SelectedAudioObject>(
    preselectedAudio ? audioToSelectedAudioObject(preselectedAudio) : {}
  );
  const [handleNoScroll, setHandleNoScroll] = useState(false);
  const [audioList, setAudioList] = useState<AudioSelectionItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const searchRef = useRef<string>('');
  const [searchState, setSearchState] = useState('');

  const filterTypesRef = useRef<AudioListFilters>({
    types: audioTypesFilterKeys || [],
    onlyLatestVersionAudio: false,
  });
  const [filterState, setFilterState] = useState<AudioListFilters>({
    types: audioTypesFilterKeys || [],
    onlyLatestVersionAudio: false,
  });
  const isMounted = useIsMounted();

  const [, startTransition] = useTransition();
  const [invalidAudioDialogDetails, setInvalidAudioDialogDetails] = useState({
    isOpen: false,
    message: '',
  });

  const songAudioListValidation = useAppSelector(getSongAudioListValidation);

  const onMaxAudioSelected = () => {
    dispatch(
      addAlertAction({
        message: `You can only select ${maxAudioCount} audio files at once`,
      })
    );
  };

  const { fetchAudio, links, setLinks } = useFetchAudio({
    setFetchingMore,
    searchRef,
    filterTypesRef,
    audioList,
    checkAllOptions,
    setAudioList: (state) => {
      if (preselectedAudio) {
        setAudioList([
          preselectedAudio,
          ...state.filter((el) => el.guid !== preselectedAudio.guid),
        ]);
      } else {
        setAudioList(state);
      }
    },
  });

  const handleInitialFetchAudio = async (search: string) => {
    setListLoading(true);
    await fetchAudio({
      search: searchRef.current,
      validationContext,
      filters: filterTypesRef.current,
    });

    if (!songAudioListValidation) {
      dispatch(fetchSongsWithAudioListSearchValidationSchema());
    }

    const shouldCancel = search !== searchRef.current || !isMounted();
    if (shouldCancel) {
      return;
    }
    if (!isAudioListScrollbarVisibile()) {
      setHandleNoScroll(true);
    }
    setListLoading(false);
  };

  const onAudioTypesLoaded = () => {
    handleInitialFetchAudio(searchState);
  };

  const [audioTypes, audioTypesLoading] = useResource(
    fetchSongAudioTypesEffect,
    getSongAudioTypes,
    {
      onResolved: onAudioTypesLoaded,
    }
  );

  useEffect(() => {
    if (!audioTypesLoading) {
      handleInitialFetchAudio(searchState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchState, filterState]);

  const handleOnSearch = (search: string) => {
    if (search !== searchRef.current) {
      setListLoading(true);
      setLinks(undefined);
      setAudioList([]);
      setSearchState(search);
      searchRef.current = search;
    }
  };

  const handleOnFilter = (filters: Partial<AudioListFilters>) => {
    setListLoading(true);
    setLinks(undefined);
    setAudioList([]);
    setFilterState({ ...filterState, ...filters });
    filterTypesRef.current = { ...filterTypesRef.current, ...filters };
  };

  useEffect(() => {
    if (handleNoScroll && !fetchingMore && !listLoading) {
      if (!isAudioListScrollbarVisibile() && links?.next) {
        fetchAudio({
          search: searchRef.current,
          validationContext,
          filters: filterTypesRef.current,
        });
      } else if (isMounted()) {
        setHandleNoScroll(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNoScroll, fetchingMore, listLoading]);

  const handleOnCheck = (state: SelectedAudioObject) => {
    setChecked(state);
    dispatch(setSelectedAudioCountAction(Object.keys(state).length));
  };

  const loading = Boolean(audioTypesLoading || listLoading || loadingProp);

  const handleOnAdd = async (data: SelectedAudioObject) => {
    try {
      setIsSubmitting(true);
      await onAdd(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidAudioSelect = (errorCode: string) => {
    startTransition(() => {
      setInvalidAudioDialogDetails({
        isOpen: true,
        message:
          getInvalidAudioErrorMessage(errorCode, validationContext) || '',
      });
    });
  };

  const filteredAudioTypes = useMemo(() => {
    if (!audioTypes) {
      return undefined;
    }
    if (!audioTypesFilterKeys) {
      return audioTypes;
    }
    const filteredAudioTypes = {};

    Object.values(audioTypes).forEach((value) => {
      if (audioTypesFilterKeys?.includes(value.key)) {
        filteredAudioTypes[value.key] = audioTypes[value.key];
      }
    });

    return filteredAudioTypes;
  }, [audioTypes, audioTypesFilterKeys]);

  return (
    <>
      <AudioSelectListComponent
        search={searchState}
        filters={filterState}
        onFilter={handleOnFilter}
        audioList={audioList}
        hasMore={Boolean(links?.next)}
        audioTypes={filteredAudioTypes || {}}
        loading={loading}
        loadingMore={fetchingMore}
        setChecked={handleOnCheck}
        checked={checked}
        isSubmitting={isSubmitting}
        maxAudioCount={maxAudioCount}
        onSearch={handleOnSearch}
        onMaxAudioFilesSelected={onMaxAudioSelected}
        fetchMore={async () =>
          fetchingMore
            ? null
            : fetchAudio({
                search: searchRef.current,
                filters: filterTypesRef.current,
              })
        }
        emptyStateMessage={emptyStateMessage}
        onCancel={onCancel}
        onAdd={() => handleOnAdd(checked)}
        songAudioListValidation={songAudioListValidation}
        disabledAudios={disabledAudios}
        onInvalidAudioSelect={handleInvalidAudioSelect}
      />
      <AudioInvalidSelectionDialog
        isOpen={invalidAudioDialogDetails.isOpen}
        handleClose={() =>
          startTransition(() =>
            setInvalidAudioDialogDetails((details) => ({
              isOpen: false,
              message: details.message,
            }))
          )
        }
        text={invalidAudioDialogDetails.message}
      />
    </>
  );
};
